import React from 'react';

import { ButtonProps, ButtonWithStatus } from '@components/Button';
import { useFollowing } from '@context/FollowingContext';
import { useRequests } from '@context/RequestsContext';
import { RequestMutationAction, useFollowRequestMutation } from '@db/Requests/Queries';
import { errorAlert } from '@helpers/Alert';
import { RequestStatus } from '@types';

type FollowButtonProps = Omit<ButtonProps, 'onPress' | 'children'> & {
  userId: string;
};

export default function FollowButton({ userId, ...props }: FollowButtonProps) {
  const { followingUserIds } = useFollowing();
  const { pendingFollowUserRequestUserIds } = useRequests();

  const [loading, setLoading] = React.useState(false);
  const [isFollowing, setIsFollowing] = React.useState(followingUserIds.has(userId));
  const [isRequested, setIsRequested] = React.useState(pendingFollowUserRequestUserIds.has(userId));
  const followMutation = useFollowRequestMutation();

  React.useEffect(() => {
    if (followingUserIds.has(userId)) {
      // Server/listener state can confirm follow status after an optimistic local update.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsFollowing(true);
    } else {
      // Server/listener state can clear follow status after external changes.
      setIsFollowing(false);
    }
  }, [userId, followingUserIds]);

  React.useEffect(() => {
    if (pendingFollowUserRequestUserIds.has(userId)) {
      // Server/listener state can confirm a pending request after an optimistic local update.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsRequested(true);
    } else {
      // Server/listener state can clear request status after external changes.
      setIsRequested(false);
    }
  }, [userId, pendingFollowUserRequestUserIds]);

  const onPress = () => {
    setLoading(true);
    followMutation.mutate(
      {
        action:
          isFollowing || isRequested ? RequestMutationAction.DELETE : RequestMutationAction.ADD,
        userId,
      },
      {
        onSuccess: (status, { action }) => {
          if (action === RequestMutationAction.DELETE) {
            setIsFollowing(false);
            setIsRequested(false);
            return;
          }

          if (status === RequestStatus.ACCEPTED) {
            setIsFollowing(true);
          }
          if (status === RequestStatus.PENDING) {
            setIsRequested(true);
          }
        },
        onError: (error) => errorAlert(error, { onAction: onPress }),
        onSettled: () => setLoading(false),
      }
    );
  };

  const status = isFollowing
    ? RequestStatus.ACCEPTED
    : isRequested
      ? RequestStatus.PENDING
      : RequestStatus.UNDETERMINED;

  const text = {
    accepted: 'Following',
    pending: 'Requested',
    undetermined: 'Follow',
  };
  const mutationPending = loading || followMutation.isPending;

  return (
    <ButtonWithStatus
      loading={mutationPending}
      disabled={mutationPending}
      onPress={onPress}
      text={text}
      status={status}
      uppercase={false}
      compact
      {...props}
    />
  );
}
